import React, { useState } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../UI/card';
import { Button } from '../UI/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../UI/tabs';
import { Loader2, Upload, List, Eye } from 'lucide-react';
import FileUploader from './FileUploader';
import PaymentPreview from './PaymentPreview';
import PaymentSummary from './PaymentSummary';
import StreamProgressPanel from './StreamProgressPanel';
import { streamNdjson } from '../../services/streamNdjson';
import { useToast } from '../../hooks/use-toast';

/**
 * VARIANTE V2 : flux simplifié de paiements de masse entreprise
 *
 * Colonnes attendues dans le fichier :
 *   PER_PERIODE    → mois (1..12)
 *   EXE_CODE       → année (YYYY)
 *   ADH_MATRIC_SAL → matricule interne
 *   CEM_MONTCOTIS  → montant
 *   PASS_CODE      → produit (FSR uniquement pour l'instant)
 *   CEM_DATE_MAJ   → date de paiement (sera la paidDate)
 *
 * L'entreprise est déduite automatiquement de chaque mutualiste.
 * Étapes : upload → prévisualisation → résultats.
 */
const EnterpriseBulkPaymentFlowV2 = ({ onCompleted }) => {
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const [activeTab, setActiveTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [processingValidation, setProcessingValidation] = useState(null);
  const [progress, setProgress] = useState(null);

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setValidationResults(null);

    try {
      setProcessing(true);
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API_URL}/payments/validate-file-v2`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setValidationResults(response.data.data.validation);
      setActiveTab('preview');

      toast({
        title: 'Fichier validé',
        description: `${response.data.data.validation.validRows} ligne(s) valide(s) sur ${response.data.data.validation.totalRows}`
      });
    } catch (error) {
      console.error('Erreur validation V2:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors de la validation du fichier'
      });
      setFile(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileRemove = () => {
    setFile(null);
    setValidationResults(null);
    setResults(null);
    setActiveTab('upload');
  };

  const handleProcessPayments = async () => {
    if (!file || !validationResults) return;

    if (validationResults.validRows === 0) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Aucune ligne valide à traiter'
      });
      return;
    }

    try {
      setProcessing(true);
      setProgress({
        current: 0, total: validationResults.validRows || 0, currentLabel: '',
        counters: { success: 0, errors: 0 },
        phase: 'processing'
      });

      const formData = new FormData();
      formData.append('file', file);

      let finalValidation = null;
      const finalResult = await streamNdjson({
        url: `${API_URL}/payments/bulk-v2?stream=1`,
        formData,
        onEvent: (evt) => {
          if (evt.type === 'start') {
            setProgress((p) => ({ ...(p || {}), total: evt.paymentsTotal || p?.total || 0 }));
          } else if (evt.type === 'payment') {
            setProgress((p) => ({
              ...(p || {}),
              current:      evt.current ?? p?.current ?? 0,
              total:        evt.total   ?? p?.total   ?? 0,
              currentLabel: evt.currentLabel || p?.currentLabel || '',
              counters: {
                success: evt.successCount ?? p?.counters?.success ?? 0,
                errors:  evt.errorCount   ?? p?.counters?.errors  ?? 0
              }
            }));
          } else if (evt.type === 'recompute-start') {
            setProgress((p) => ({ ...(p || {}), phase: 'recompute', currentLabel: `Recalcul de ${evt.matricules || 0} police(s) FSR...` }));
          } else if (evt.type === 'recompute-done') {
            setProgress((p) => ({ ...(p || {}), phase: 'finalizing', currentLabel: 'Finalisation...' }));
          } else if (evt.type === 'end') {
            finalValidation = evt.validation || null;
          }
        }
      });

      setResults(finalResult);
      setProcessingValidation(finalValidation);
      setActiveTab('results');

      toast({
        title: 'Traitement terminé',
        description: `${finalResult?.successCount || 0} paiement(s) traité(s) avec succès`
      });
    } catch (error) {
      console.error('Erreur traitement V2:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Erreur lors du traitement des paiements'
      });
    } finally {
      setProcessing(false);
    }
  };

  const progressForPanel = progress ? {
    current:      progress.current,
    total:        progress.total,
    currentLabel: progress.currentLabel,
    metrics: [
      { label: 'Succès',  value: progress.counters?.success || 0, tone: 'emerald' },
      { label: 'Erreurs', value: progress.counters?.errors  || 0, tone: (progress.counters?.errors || 0) > 0 ? 'red' : 'gray' }
    ]
  } : null;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="upload" disabled={processing}>
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </TabsTrigger>
        <TabsTrigger value="preview" disabled={!validationResults || processing}>
          <Eye className="w-4 h-4 mr-2" />
          Prévisualisation
        </TabsTrigger>
        <TabsTrigger value="results" disabled={!results}>
          <List className="w-4 h-4 mr-2" />
          Résultats
        </TabsTrigger>
      </TabsList>

      {/* Étape 1 : Upload */}
      <TabsContent value="upload" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Importer le fichier de paiements (V2)</CardTitle>
            <CardDescription>
              Colonnes attendues : <code>PER_PERIODE</code>, <code>EXE_CODE</code>,{' '}
              <code>ADH_MATRIC_SAL</code>, <code>CEM_MONTCOTIS</code>,{' '}
              <code>PASS_CODE</code>, <code>CEM_DATE_MAJ</code>. Seul le produit{' '}
              <strong>FSR</strong> est pris en charge pour l'instant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FileUploader
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              acceptedFormats=".csv,.xlsx"
              type="bulk"
              loading={processing}
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 2 : Prévisualisation */}
      <TabsContent value="preview" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Prévisualisation des paiements</CardTitle>
            <CardDescription>
              Vérifiez les données avant de lancer le traitement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PaymentPreview
              validationResults={validationResults}
              type="bulk-v2"
            />

            {processing && progressForPanel && (
              <div className="mt-6">
                <StreamProgressPanel
                  title={
                    progress?.phase === 'recompute'   ? 'Recalcul des polices FSR' :
                    progress?.phase === 'finalizing'  ? 'Finalisation'             :
                                                       'Traitement des paiements'
                  }
                  progress={progressForPanel}
                  barColor="indigo"
                />
              </div>
            )}

            <div className="flex gap-3 mt-6 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setActiveTab('upload')}
                disabled={processing}
              >
                Retour
              </Button>
              <Button
                onClick={handleProcessPayments}
                disabled={processing || !validationResults || validationResults.validRows === 0}
                className="flex-1"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  `Traiter ${validationResults?.validRows || 0} paiement(s)`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 3 : Résultats */}
      <TabsContent value="results" className="space-y-6 mt-4">
        <PaymentSummary
          results={results}
          validationRows={processingValidation?.rows || []}
          type="bulk-v2"
        />

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              handleFileRemove();
              setResults(null);
              setProcessingValidation(null);
              setActiveTab('upload');
            }}
          >
            Nouveau traitement
          </Button>
          {onCompleted && (
            <Button onClick={() => onCompleted(results)}>
              Terminer
            </Button>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default EnterpriseBulkPaymentFlowV2;
